import { useContext, useEffect, useState } from "react";
import Button from "carbon-react/esm/components/button/__next__";
import Loader from "carbon-react/esm/components/loader/__next__";
import Message from "carbon-react/esm/components/message";

import { ApplicationApiError } from "../api/applicationApi";
import {
  advanceSageMasterDataSync,
  getActiveSageSyncRun,
  getSageSettings,
  isActiveSageSyncStatus,
  startSageMasterDataSync,
  type SageSettingsResponse,
  type SageSyncResponse,
} from "../api/sageSettingsApi";
import { EmbeddedSessionContext } from "../auth/EmbeddedSessionProvider";
import { StatusPill } from "../components/StatusPill";

export function SageSettingsPage() {
  const { api } = useContext(EmbeddedSessionContext);
  const [state, setState] = useState<
    { status: "loading" } | { status: "ready"; value: SageSettingsResponse } | { status: "error" }
  >({ status: "loading" });
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SageSyncResponse | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const activeRun = state.status === "ready" ? getActiveSageSyncRun(state.value.runs) : null;

  useEffect(() => {
    if (!api) return;
    let active = true;
    void getSageSettings(api)
      .then((value) => active && setState({ status: "ready", value }))
      .catch(() => active && setState({ status: "error" }));
    return () => { active = false; };
  }, [api]);

  useEffect(() => {
    if (!api || !activeRun) return;
    let active = true;
    let advancing = false;
    let timeoutId: number | undefined;
    const advance = async () => {
      if (advancing) return;
      advancing = true;
      try {
        const result = await advanceSageMasterDataSync(api, activeRun.id);
        const value = await getSageSettings(api);
        if (!active) return;
        setState({ status: "ready", value });
        setSyncError(null);
        if (!isActiveSageSyncStatus(result.status)) setSyncResult(result);
      } catch (error) {
        if (!active) return;
        if (!(error instanceof ApplicationApiError && error.code === "SAGE_SYNC_STEP_IN_PROGRESS")) {
          const reference = error instanceof ApplicationApiError ? ` Reference: ${error.correlationId}` : "";
          setSyncError(`A synchronization step could not be completed and will be retried.${reference}`);
        }
      } finally {
        advancing = false;
        if (active) timeoutId = window.setTimeout(() => void advance(), 5_000);
      }
    };
    void advance();
    return () => {
      active = false;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [api, activeRun?.id]);

  if (state.status === "loading") return <Loader loaderLabel="Loading Sage connection" size="medium" />;
  if (state.status === "error") {
    return <Message open title="Sage connection could not be loaded" variant="error">Reopen Loan Management from Sage Intacct.</Message>;
  }

  const synchronize = async () => {
    if (!api || syncing) return;
    setSyncing(true);
    setSyncError(null);
    setSyncResult(null);
    try {
      const result = await startSageMasterDataSync(api);
      const value = await getSageSettings(api);
      setState({ status: "ready", value });
      if (!isActiveSageSyncStatus(result.status)) setSyncResult(result);
    } catch (error) {
      const reference = error instanceof ApplicationApiError ? ` Reference: ${error.correlationId}` : "";
      setSyncError(`Master data synchronization could not be started.${reference}`);
    } finally {
      setSyncing(false);
    }
  };

  const { connection, currentContext, recordTypes, runs } = state.value;
  return (
    <div className="settings-stack">
      {syncError && <Message open title="Synchronization failed" variant="error">{syncError}</Message>}
      {(syncing || activeRun) && (
        <Message open title="Synchronization in progress" variant="info">
          {activeRun
            ? `${activeRun.completedItems} of ${activeRun.totalItems} record types completed. This page advances one record type at a time.`
            : "Preparing accounts, journals, and dimensions."}
        </Message>
      )}
      {syncResult && (
        <Message
          open
          title={syncResult.status === "succeeded"
            ? "Synchronization completed"
            : syncResult.status === "partial"
            ? "Synchronization partially completed"
            : "Synchronization failed"}
          variant={syncResult.status === "succeeded" ? "info" : syncResult.status === "partial" ? "warning" : "error"}
        >
          {syncResult.recordsAdded} added, {syncResult.recordsUpdated} updated, and {syncResult.recordsDeactivated} deactivated.
          {syncResult.warningCount > 0 || syncResult.failureCount > 0
            ? ` ${syncResult.warningCount} warnings and ${syncResult.failureCount} failures.`
            : ""}
        </Message>
      )}
      <section className="settings-section" aria-labelledby="sage-connection-heading">
        <div className="settings-section__heading">
          <h2 id="sage-connection-heading">Connection</h2>
          <StatusPill label={connection.status} tone={connection.status === "connected" ? "positive" : "warning"} />
        </div>
        <dl className="settings-summary">
          <Summary label="Company ID" value={connection.companyId} />
          <Summary label="Environment" value={connection.environment} />
          <Summary label="Sage user" value={currentContext.userId} />
          <Summary label="Location" value={currentContext.locationId ?? "Top level"} />
          <Summary label="Last authenticated" value={formatDate(connection.lastSuccessfulAuthenticationAt)} />
          <Summary label="Last API request" value={formatDate(connection.lastSuccessfulApiRequestAt)} />
          <Summary label="Last successful sync" value={formatDate(connection.lastSuccessfulSyncAt)} />
          <Summary label="Application / protocol" value={`${state.value.applicationVersion} / ${state.value.protocolVersion}`} />
        </dl>
      </section>

      <section className="settings-section" aria-labelledby="sage-sync-heading">
        <div className="settings-section__heading">
          <div>
            <h2 id="sage-sync-heading">Master data</h2>
            <p>Accounts, journals, standard dimensions, and company-defined dimensions.</p>
          </div>
          <Button
            disabled={syncing || activeRun !== null}
            onClick={() => void synchronize()}
            type="button"
            variant="default"
            variantType="primary"
          >
            {syncing || activeRun ? "Synchronizing" : "Sync all master data"}
          </Button>
        </div>
        <div className="availability-list">
          {recordTypes.map((recordType) => (
            <div className="availability-row" key={recordType.id}>
              <span>{recordType.label}</span>
              <StatusPill label={recordType.available ? "Available" : "Unavailable"} tone={recordType.available ? "positive" : "neutral"} />
              {!recordType.available && <span className="muted-text">{recordType.unavailableReason}</span>}
            </div>
          ))}
        </div>
      </section>

      <section className="settings-section" aria-labelledby="sage-history-heading">
        <h2 id="sage-history-heading">Synchronization history</h2>
        {runs.length === 0 ? (
          <p className="muted-text">No synchronization attempts have been recorded.</p>
        ) : (
          <div className="sync-history">
            {runs.map((run) => (
              <div className="sync-history__row" key={run.id}>
                <div><strong>{run.scope === "full" ? "Full sync" : "Selected sync"}</strong><br /><span className="muted-text">{formatDate(run.createdAt)}</span></div>
                <StatusPill label={run.status} tone={run.status === "succeeded" ? "positive" : run.status === "failed" ? "negative" : "warning"} />
                <span>{run.completedItems}/{run.totalItems} types · +{run.recordsAdded} / ~{run.recordsUpdated} / -{run.recordsDeactivated}</span>
                <span>{run.warningCount} warnings · {run.failureCount} failures</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return <div><dt>{label}</dt><dd>{value}</dd></div>;
}

function formatDate(value: string | null): string {
  if (!value) return "Not yet";
  const date = new Date(value);
  if(Number.isNaN(date.valueOf()))return "Unavailable";
  const datePart=`${date.getMonth()+1}/${date.getDate()}/${date.getFullYear()}`;
  const timePart=new Intl.DateTimeFormat(undefined,{hour:"numeric",minute:"2-digit"}).format(date);
  return `${datePart}, ${timePart}`;
}
